// Props
// Features:
// - Container for all interactive and static props in the cabin
// - Manages shared materials (props shader, physics material)
// - Instantiates specific prop components (Radio, Cat, FirePlace, etc.)
// - Places atmospheric steam effects

import { useMemo } from 'react'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'
import { getPointLightUniforms } from '../../utils/lighting'

import vertexShaderInside from '../shaders/cabin/inside/vertex.glsl'
import fragmentShaderInside from '../shaders/cabin/inside/fragment.glsl'
import propsTextureURL from '/textures/props_texture.webp'

// Child Components
import FirePlace from './FirePlace'
import Cat from './Cat'
import Radio from './Radio'
import Clock from './Clock'
import SteamSmoke from './SteamSmoke'
import LightSwitcher from './LightSwitcher'
import Candle from './Candle'
import Guitar from './Guitar'
import TableLamp from './TableLamp'

const STEAM_CONFIGS = [
    {
        position: [-1.86, 1.38, -3.16],
        rotation: [0, -0.73, 0],
        opacity: 0.6,
        textureScale: 1.3,
        timeOffset: 1.5,
        twist: 0.85,
        width: 0.16,
        height: 0.37,
    },
    {
        position: [-2.32, 1.38, -2.86],
        rotation: [0, 1.04, 0],
        opacity: 0.6,
        textureScale: 1.2,
        timeOffset: 4.5,
        twist: 0.95,
        width: 0.16,
        height: 0.37,
    },
    {
        position: [-1.6, 0.35, -2.67],
        rotation: [0, -0.81, 0],
        opacity: 0.3,
        textureScale: 0.9,
        timeOffset: 2.5,
        twist: 0.5,
        width: 0.22,
        height: 0.34,
    },
    {
        position: [-1.86, 0.35, -2.29],
        rotation: [0, -0.29, 0],
        opacity: 0.3,
        textureScale: 0.8,
        timeOffset: 3.5,
        twist: 0.8,
        width: 0.22,
        height: 0.34,
    },
]

export default function Props({ nodes, lightControls, physicsMaterial }) {
    const lightsOn = useStore((state) => state.lightsOn)

    // Textures
    const propsTexture = useTexture(propsTextureURL, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace
        texture.flipY = false
    })

    // Materials
    const materialInside = useMemo(() => {
        const pointFactor = lightsOn ? 1.0 : 0.1
        const ambientFactor = lightsOn ? 1.0 : 0.2

        return new THREE.ShaderMaterial({
            uniforms: {
                uColor: new THREE.Uniform(new THREE.Color('#ff0000')),
                uMap: { value: propsTexture },
                uIntensity: {
                    value: lightControls.ambientIntensity * ambientFactor,
                },
                ...getPointLightUniforms(lightControls, pointFactor),
            },
            vertexShader: vertexShaderInside,
            fragmentShader: fragmentShaderInside,
        })
    }, [propsTexture, lightControls, lightsOn])

    return (
        <group>
            {/* Main Props Mesh */}
            <mesh
                castShadow
                receiveShadow
                geometry={nodes.props.geometry}
                material={materialInside}
            />

            {/* Interactive Props */}
            <LightSwitcher
                nodes={nodes}
                material={materialInside}
                physicsMaterial={physicsMaterial}
            />
            <FirePlace nodes={nodes} texture={propsTexture} />
            <Cat nodes={nodes} material={materialInside} />
            <Radio nodes={nodes} material={materialInside} />
            <Clock nodes={nodes} material={materialInside} />
            <Candle nodes={nodes} physicsMaterial={physicsMaterial} />
            <Guitar
                nodes={nodes}
                material={materialInside}
                physicsMaterial={physicsMaterial}
            />
            <TableLamp nodes={nodes} physicsMaterial={physicsMaterial} />

            {/* Atmosphere */}
            {STEAM_CONFIGS.map((config, index) => (
                <SteamSmoke key={index} {...config} />
            ))}
        </group>
    )
}
